import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/Shared/Toast';
import { Button, Spinner } from 'reactstrap';
import clsx from 'clsx';

import { getTimeline, contractAction } from 'api/actions/contracts';
import EndorsePagare from 'components/Modals/EndorsePagare';
import useMetamask from 'hooks/useMetamask';
import Deposit from 'components/Modals/Profile/Deposit';
import { fetcher } from 'api/client';
import { parseDate } from 'utils';
import { getAcreedor } from 'api/actions/blockchain';

function Event(props) {
    const { event = {} } = props;
    return (
        <div className={clsx('timeline-item', event.status)}>
            <div className={clsx(event.status === 'executed' ? 'timeline-executed' : 'd-none')}>
                <i className="fas fa-check"></i></div>
            <h1>
                <b>{event.title}</b>
            </h1>
            <p>
                {event.subtitle}
            </p>
        </div>
    )
}

const metamaskUrl = 'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn';

export default function Blockchain({ contract = {}, contractQueryId }) {
    const { t } = useTranslation();

    const { data: blockchainEvents = [] } = useQuery([...contractQueryId, 'blockchain'], ({ queryKey: [, contractId] }) => fetcher(getTimeline(contractId, 'blockchain')), {
        enabled: !!contract._id,
    })
    const { data: { balance } = {} } = useQuery(['contract-balance'], () => fetcher(contractAction(contract._id, 'getbalance', {})), { refetchOnWindowFocus: false, enabled: !contract.isPagare });
    const { data: { acreedor } = {} } = useQuery(['contract-acreedor'], () => fetcher(getAcreedor(contract.pagareId)), { refetchOnWindowFocus: false, enabled: contract.isPagare });
    // const { data: { md5 } = {} } = useQuery(['contract-md5'], () => fetcher(contractAction(contract._id, 'getmd5', {})), { refetchOnWindowFocus: false, enabled: contract.isPagare });

    const { metamask, burnToken, isCanceling, isEndorsing, wallet, providerAddress, isConnected, reconnect } = useMetamask(process.env.REACT_APP_PAGARE_ADDRESS, contract._id, contract.pagareId);

    const burnPagare = () => {
        burnToken(contract.pagareId).then((mutation) => {
            if (mutation) {
                toast.success(t('blockchain.endorsed'));
            }
        }).catch((error) => {
            toast.error(t(`errors.${error.message}`))
        })
    }

    const [depositOpen, setdepositOpen] = useState(false)
    const [endorseOpen, setEndorseOpen] = useState(false)

    const toggleEndorse = () => {
        setEndorseOpen(!endorseOpen);
    }

    function copyToClipboard() {
        navigator.clipboard.writeText(contract._address);
        toast.success(t('common.copied_clipboard'));
    };

    const goToContract = () => {
        if (contract.isPagare) {
            window.open(`${process.env.REACT_APP_ETHERSCAN_URL}/token/${process.env.REACT_APP_PAGARE_ADDRESS}?a=${contract.pagareId}`)
        } else {
            window.open(`${process.env.REACT_APP_ETHERSCAN_URL}/address/${contract._address}`)
        }
    }

    const fundContract = () => innerToggle();

    const innerToggle = () => { setdepositOpen(!depositOpen) }

    const installMetamask = () => window.open(metamaskUrl);

    const isPagareOwner = acreedor?.toLowerCase() === providerAddress?.toLowerCase();

    let amount = null;

    if (contract) amount = contract.elements?.find((element) => Object.keys(element?.smartclause || {}).length)?.smartclause?.amount;

    const isFunded = balance >= amount;
    const endorsableTo = contract?.endorsableTo;
    const creationDate = blockchainEvents.find((t) => t.type === 'blockchain-create-contract-init')
    const signDate = blockchainEvents.find((t) => t.type === 'blockchain-sign-init')
    const signAll = blockchainEvents.find((t) => t.type === 'blockchain-sign-all')
    const signInit = blockchainEvents.find((t) => t.type === 'blockchain-sign')
    const fundDate = blockchainEvents.find((t) => t.type === 'blockchain-fund-contract-init')
    const burned = blockchainEvents.find((t) => t.type === 'blockchain-burn-token')

    const events = useMemo(() => [
        {
            title: t('blockchain.registered'),
            subtitle: creationDate ? parseDate(creationDate?.eventDate, 'Pp') : '', status: creationDate ? 'executed' : 'execute'
        },

        {
            title: t('blockchain.toFund'),
            subtitle: (isFunded || signAll) ? parseDate(fundDate?.eventDate, 'Pp') : '', status: (isFunded || signAll) ? 'executed' : 'execute'
        },

        {
            title: t('blockchain.funded'),
            subtitle: (isFunded || signAll) ? parseDate(fundDate?.eventDate, 'Pp') : '', status: (isFunded || signAll) ? 'executed' : 'executing'
        },

        {
            title: t('blockchain.waiting_sign'),
            subtitle: signAll ? parseDate(signAll?.eventDate, 'Pp') : '', status: signAll ? 'executed' : isFunded ? 'execute' : 'executing'
        },

        {
            title: t('blockchain.signed_by_all'),
            subtitle: signAll ? parseDate(signAll?.eventDate, 'Pp') : '', status: signAll ? 'executed' : 'executing'
        }
    ], [t, creationDate, isFunded, signAll, fundDate]);

    return (<>
        <Deposit contract={contract} isOpen={depositOpen} toggle={innerToggle} />
        <EndorsePagare
            isOpen={endorseOpen}
            toggle={toggleEndorse}
            pagareId={contract?.pagareId}
            contractId={contract?._id}
            acreedor={acreedor}
        />
        <div className="p-5" style={{ overflowY: 'scroll' }}>
            <div className="text-center">
                {contract.isPagare ?
                    <img src="/images/ui/pagare.svg" alt="" width="100" className="mb-3" /> :
                    <img src="/images/ui/smart-bulb.png" alt="smart" width="120px" />
                }
                <h3><b>{contract.isPagare ? t('blockchain.pagare') : t('blockchain.smartContract')}</b></h3>
            </div>
            <div className="text-center text-smaller">
                {isConnected ?
                    <span><i className="fas fa-dot-circle pulse-animation text-success"></i>
                        {' '}Metamask {t('blockchain.online')} ({providerAddress || (<b className="pointer" onClick={reconnect}>{t('blockchain.connect')}</b>)})
                    </span> :
                    <div>
                        <i className="fas fa-dot-circle pulse-animation text-danger"></i>
                        {' '}Metamask {t('blockchain.offline')}
                        <div className="clear-fix my-2"></div>
                        <a target="_blank" rel="noreferrer" href={metamaskUrl}>{t('blockchain.install_here')}</a>
                    </div>
                }
            </div>
            <div className="mt-5">
                <p><b>{t('blockchain.name')}</b></p>
                <p>{contract.name}</p>
            </div>
            {contract.pagareId &&
                <>
                    <hr className="mb-3" />
                    <div className="mt-5">
                        <p><b>{t('blockchain.pagareId')}</b></p>
                        <p>{contract.pagareId}</p>
                    </div>
                </>
            }

            <hr className="mb-3" />
            <div className="mt-2">
                <p><b>{t('blockchain.owner')}</b></p>
                <p>{contract.user?.givenName} {contract.user?.familyName}</p>
            </div>

            <hr className="mb-3" />
            <div className="mt-2">
                <p><b>{t('blockchain.participants')}</b></p>
                {contract.participants?.map((p, index) => <p key={p._id}>{index + 1}. {p.name} {p.obligation === 'individual' ? p.last_name : ''}</p>)}
            </div>

            <hr className="mb-3" />
            <div className="mt-2">
                <p><b>{t('blockchain.address')}</b></p>
                <p className="text-center text-smaller pointer" onClick={copyToClipboard}>{contract._address}{/*' '}<i className="fas fa-clipboard"></i>*/}</p>
                <Button block color="secondary" className="mb-2" onClick={goToContract}>
                    <img src="/images/ui/ethereum-brands.svg" width="10px" alt="" />{' '}{t('blockchain.view_ethereum')}
                </Button>
            </div>

            <hr className="mb-3" />
            <div className="">
                <p><b>{t('blockchain.status')}</b></p>

                <div hidden={!contract.isPagare}>
                    <p><b>MD5</b></p>
                    <p>
                        {/* {md5 || <Spinner size="sm" color="secondary" />} */}
                    </p>
                    <p><b>{t('blockchain.acreedor')}</b></p>
                    <p className="text-center">
                        {acreedor ? <span className="text-smaller pointer">{acreedor}</span> : <Spinner size="sm" color="secondary" />}
                    </p>
                    <p className={clsx(endorsableTo || 'd-none')}><b>{t('blockchain.endorsableTo')}</b></p>
                    <p className={clsx(endorsableTo || 'd-none')}>
                        {endorsableTo}
                    </p>
                </div>

                <div hidden={contract.isPagare} className="timeline mt-2">
                    {events?.map((event, key) => (
                        <Event {...event} key={key} />
                    ))}
                </div>

            </div>
            <hr className="mb-3" />
            <div hidden={!isConnected}>
                <div hidden={!contract.isPagare || !isPagareOwner}>
                    <Button disabled={isCanceling || isEndorsing || !contract.blockchainConfirmed} className="btn btn-block text-center mb-2" onClick={toggleEndorse}>
                        <i className="fas fa-exchange-alt"></i>{' '}{t('blockchain.endorse_promissory_note')}
                    </Button>
                    <Button disabled={isCanceling || isEndorsing || !contract.blockchainConfirmed} className="btn btn-block btn-danger text-center mb-2" onClick={burnPagare}>
                        <i className="fas fa-ban"></i>{' '}{t('blockchain.cancel_pagare')}
                        <Spinner hidden={!isCanceling && !isEndorsing} />
                    </Button>
                </div>
                <Button hidden={contract.isPagare || !contract.blockchainConfirmed} className="btn btn-block btn-primary text-center mb-2" onClick={fundContract}>
                    <i className="fas fa-dollar-sign"></i>{' '}{t('blockchain.fund_contract')}
                </Button>

            </div>

            <Button hidden={isConnected} className="btn btn-primary btn-block" onClick={installMetamask}>{t('blockchain.install_metamask')}</Button>

        </div>
    </>
    )
};

